import React, { useState, useEffect, useCallback } from 'react';
import { Cpu, HardDrive, Wifi, Server, Loader2, Clock } from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  Label,
} from 'recharts';
import { LineChart as LineChartIcon } from 'lucide-react';
import baseapi from '../../../services/baseapi';
// import { ReplicationView } from './ReplicationView';

const formatUptime = (seconds: number | null | undefined) => {
  if (seconds == null || isNaN(seconds) || seconds <= 0) return '—';
  const sec = Math.floor(seconds);
  const days = Math.floor(sec / 86400);
  const hours = Math.floor((sec % 86400) / 3600);
  const minutes = Math.floor((sec % 3600) / 60);

  if (days > 0) {
    return `${days}d ${hours}h`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m ${sec % 60}s`;
};

const fmtSize = (mb: number | null | undefined, unit?: string) => {
  if (mb == null || isNaN(mb) || mb <= 0) return '—';
  if (unit === 'gb') return `${mb.toFixed(2)} GB`;
  if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
  if (mb >= 1) return `${mb.toFixed(2)} MB`;
  return `${(mb * 1024).toFixed(0)} KB`;
};

const fmtNet = (val: number | null | undefined, unit?: string) => {
  if (val == null || isNaN(val) || val <= 0) return '—';
  if (unit === 'mb_per_sec') {
    if (val >= 1024) return `${(val / 1024).toFixed(2)} GB/s`;
    if (val >= 1) return `${val.toFixed(2)} MB/s`;
    if (val >= 0.001) return `${(val * 1024).toFixed(2)} KB/s`;
    return `${(val * 1024 * 1024).toFixed(1)} B/s`;
  }
  if (val >= 1024 * 1024) return `${(val / (1024 * 1024)).toFixed(2)} MB/s`;
  if (val >= 1024) return `${(val / 1024).toFixed(2)} KB/s`;
  if (val >= 1) return `${val.toFixed(2)} MB/s`;
  return `${val.toFixed(3)} MB/s`;
};

interface MetricsTabProps {
  activeCluster: string;
  timeRange: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
}

const ScrollableLegend = (props: any) => {
  const { payload, hoveredSeries, onHoverSeries } = props;
  if (!payload || payload.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 max-h-14 overflow-y-auto custom-scroll px-2 py-1 relative z-0">
      {payload.map((entry: any, index: number) => {
        const seriesKey = entry.dataKey || entry.value;
        const isHovered = hoveredSeries === seriesKey;
        const isDimmed = hoveredSeries !== null && !isHovered;
        return (
          <div
            key={`legend-item-${index}`}
            onMouseEnter={() => onHoverSeries && onHoverSeries(seriesKey)}
            onMouseLeave={() => onHoverSeries && onHoverSeries(null)}
            className={`flex items-center gap-1.5 shrink-0 cursor-pointer transition-all duration-200 ${isDimmed ? 'opacity-30 scale-95' : 'opacity-100 scale-95'
              }`}
          >
            <span
              className="w-2 h-2 rounded-full inline-block shrink-0"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-gray-700 dark:text-gray-200 text-[9px] truncate max-w-[130px]" title={entry.value}>
              {entry.value}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const ChartTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border border-gray-200 dark:border-gray-700/80 rounded-lg p-2.5 shadow-xl text-[11px] text-gray-900 dark:text-gray-100 flex flex-col gap-1.5 min-w-[130px] z-50">
        {label !== undefined && label !== null && label !== "" && (
          <div className="text-gray-700 dark:text-gray-300 font-bold border-b border-gray-100 dark:border-gray-800 pb-1 mb-0.5 text-[11px] truncate">
            {label}
          </div>
        )}
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex justify-between items-center gap-4">
            <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300 font-medium">
              <span
                className="inline-block w-2 h-2 rounded-full shrink-0"
                style={{ backgroundColor: entry.color || entry.stroke || entry.fill || "#f97316" }}
              />
              {entry.name || entry.dataKey}
            </span>
            <span className="font-bold text-gray-900 dark:text-white font-mono">
              {typeof entry.value === 'number' ? entry.value.toLocaleString() : (entry.value ?? '-')}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export const MetricsTab: React.FC<MetricsTabProps> = ({
  activeCluster,
  timeRange,
  clientId,
  refreshKey,
}) => {
  const [loading, setLoading] = useState(true);
  const [hostGraphData, setHostGraphData] = useState<any>(null);

  // const isES = ['opensearch', 'elasticsearch'].includes((dbType || '').toLowerCase());

  // Legend Hover States
  const [hoveredCpu, setHoveredCpu] = useState<string | null>(null);
  const [hoveredMem, setHoveredMem] = useState<string | null>(null);
  const [hoveredNet, setHoveredNet] = useState<string | null>(null);
  const [hoveredDisk, setHoveredDisk] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);

    const minutes = parseInt(timeRange || '60');

    const params: any = {
      cluster_id: activeCluster,
      minutes,
    };
    if (clientId) params.client_id = clientId;

    try {
      const res = await baseapi.get('/db_monitor_dashboard/graph/host', { params });
      setHostGraphData(res.data?.data || res.data);
    } catch (err) {
      console.warn('MetricsTab fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [activeCluster, timeRange, clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const labels = hostGraphData?.labels || [];
  const series = hostGraphData?.series || {};

  const formatTimeLabels = (lbls: string[]) => {
    if (!lbls || lbls.length === 0) return [];
    const minutes = parseInt(timeRange || '60');
    const seen: { [key: string]: number } = {};
    return lbls.map((lbl) => {
      const d = new Date(lbl);
      let base = isNaN(d.getTime())
        ? lbl
        : minutes > 1440
          ? d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
          : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

      if (seen[base] !== undefined) {
        seen[base] += 1;
        base = `${base}${' '.repeat(seen[base])}`;
      } else {
        seen[base] = 0;
      }
      return base;
    });
  };

  const units = hostGraphData?.units || {};
  const isNetMbSec = units.host_net_rx_bytes === 'mb_per_sec';
  const isMemGb = units.host_mem_used_mb === 'gb' || units.host_mem_total_mb === 'gb';

  const timeLabels = formatTimeLabels(labels);
  const formattedData = labels.map((_: string, idx: number) => {
    const rawRx = series.host_net_rx_bytes?.[idx] || 0;
    const rawTx = series.host_net_tx_bytes?.[idx] || 0;
    const rawDiskUsed = series.disk_used_mb?.[idx] || 0;
    const rawDiskTotal = series.disk_total_mb?.[idx] || 0;

    const netRxMB = isNetMbSec
      ? Number(rawRx.toFixed(4))
      : rawRx > 1000
        ? Number((rawRx / (1024 * 1024)).toFixed(2))
        : Number(rawRx.toFixed(2));
    const netTxMB = isNetMbSec
      ? Number(rawTx.toFixed(4))
      : rawTx > 1000
        ? Number((rawTx / (1024 * 1024)).toFixed(2))
        : Number(rawTx.toFixed(2));

    const diskUsedGB = Number((rawDiskUsed / 1024).toFixed(2));
    const diskTotalGB = Number((rawDiskTotal / 1024).toFixed(2));

    const rawMemTotal = series.host_mem_total_mb?.[idx] || 0;
    const rawSwapUsed = series.host_swap_used_mb?.[idx] || 0;

    const memTotalGB = isMemGb
      ? Number(rawMemTotal.toFixed(2))
      : Number((rawMemTotal / 1024).toFixed(2));
    const swapUsedGB = isMemGb
      ? Number(rawSwapUsed.toFixed(2))
      : Number((rawSwapUsed / 1024).toFixed(2));

    return {
      time: timeLabels[idx],
      user_cpu: series.host_cpu_user_pct?.[idx] || 0,
      sys_cpu: series.host_cpu_sys_pct?.[idx] || 0,
      iowait_cpu: series.host_cpu_iowait_pct?.[idx] || 0,
      steal_cpu: series.host_cpu_steal_pct?.[idx] || 0,
      mem_used: series.host_mem_used_pct?.[idx] || 0,
      mem_total_mb: memTotalGB,
      swap_used_mb: swapUsedGB,
      net_rx: netRxMB,
      net_tx: netTxMB,
      disk_used: diskUsedGB,
      disk_total: diskTotalGB,
    };
  });

  const hasData = (arr: any[]) => {
    if (!arr || arr.length === 0) return false;
    return arr.some((item) => Object.keys(item).some((k) => k !== 'time' && typeof item[k] === 'number' && item[k] > 0));
  };

  const getOpacity = (hovered: string | null, key: string) => {
    if (hovered === null) return 1;
    return hovered === key ? 1 : 0.1;
  };

  if (loading) {
    return (
      <div className="absolute inset-0 bg-white/60 dark:bg-gray-900/50 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-xl">
        <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
      </div>
    );
  }

  const summary = hostGraphData?.summary;
  const latest = summary?.latest;
  const peak = summary?.peak;
  const average = summary?.average;

  const uptimeSec =
    latest?.uptime_sec ??
    summary?.uptime_sec ??
    hostGraphData?.latest?.uptime_sec;

  const latestCpuTotal =
    latest?.host_cpu_user_pct != null || latest?.host_cpu_sys_pct != null
      ? (Number(latest.host_cpu_user_pct) || 0) + (Number(latest.host_cpu_sys_pct) || 0)
      : null;

  const avgCpuTotal =
    average?.host_cpu_user_pct != null || average?.host_cpu_sys_pct != null
      ? (Number(average.host_cpu_user_pct) || 0) + (Number(average.host_cpu_sys_pct) || 0)
      : null;

  const peakCpuTotal =
    peak?.host_cpu_user_pct != null || peak?.host_cpu_sys_pct != null
      ? (Number(peak.host_cpu_user_pct) || 0) + (Number(peak.host_cpu_sys_pct) || 0)
      : null;

  const latestNetTotal =
    latest?.host_net_rx_bytes != null || latest?.host_net_tx_bytes != null
      ? (Number(latest.host_net_rx_bytes) || 0) + (Number(latest.host_net_tx_bytes) || 0)
      : null;

  const avgNetTotal =
    average?.host_net_rx_bytes != null || average?.host_net_tx_bytes != null
      ? (Number(average.host_net_rx_bytes) || 0) + (Number(average.host_net_tx_bytes) || 0)
      : null;

  const peakNetTotal =
    peak?.host_net_rx_bytes != null || peak?.host_net_tx_bytes != null
      ? (Number(peak.host_net_rx_bytes) || 0) + (Number(peak.host_net_tx_bytes) || 0)
      : null;

  // Disk metrics for summary card
  const diskTotalMb = latest?.disk_total_mb ?? average?.disk_total_mb ?? 0;
  const diskUsedMb = latest?.disk_used_mb ?? average?.disk_used_mb ?? 0;
  const diskFreeMb = Math.max(0, diskTotalMb - diskUsedMb);
  const diskUsedPctVal =
    latest?.disk_used_pct != null
      ? Number(latest.disk_used_pct)
      : average?.disk_used_pct != null
        ? Number(average.disk_used_pct)
        : 0;
  const diskThresholdLabel =
    diskUsedPctVal >= 90
      ? `${diskUsedPctVal.toFixed(0)}% (Critical > 90%)`
      : diskUsedPctVal >= 80
        ? `${diskUsedPctVal.toFixed(0)}% (Above 80% Threshold)`
        : `${diskUsedPctVal.toFixed(0)}% (Near 80% Threshold)`;

  // Memory metrics for summary card
  const memUsedMb = latest?.host_mem_used_mb ?? average?.host_mem_used_mb ?? 0;
  const memTotalMb = latest?.host_mem_total_mb ?? average?.host_mem_total_mb ?? 0;
  const memUsedPctVal =
    latest?.host_mem_used_pct != null
      ? Number(latest.host_mem_used_pct)
      : average?.host_mem_used_pct != null
        ? Number(average.host_mem_used_pct)
        : 0;

  // Network metrics for summary card
  const rxBytes = Number(latest?.host_net_rx_bytes) || 0;
  const txBytes = Number(latest?.host_net_tx_bytes) || 0;
  const totalNetBytes = rxBytes + txBytes;
  const rxBarPct = totalNetBytes > 0 ? (rxBytes / totalNetBytes) * 100 : 50;
  const txBarPct = totalNetBytes > 0 ? (txBytes / totalNetBytes) * 100 : 50;

  return (
    <div className="space-y-3.5 relative">
      {/* Section 1: System & Operating Host Metrics */}
      <div className="space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded bg-orange-500 text-white shadow-md">
              <LineChartIcon className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">
                Server Performance
              </h2>
              {summary && (
                <span className="text-[11px] text-gray-500 dark:text-gray-100">
                  Telemetry Summary: Latest, Peak &amp; Average
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {uptimeSec != null && uptimeSec > 0 && (
              <div className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30">
                <Clock className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                <span>Uptime:</span>
                <span className="font-bold font-mono">{formatUptime(uptimeSec)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Host Summary Section (Peak, Avg, Latest) - Cards View */}
        {summary && (
          <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl space-y-3.5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-2 border-b border-gray-100 dark:border-gray-700/50">
              <div className="flex items-center gap-2">
                <Server className="w-4 h-4 text-orange-500" />
                <h3 className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">
                  Host Metrics Summary
                </h3>
              </div>
            </div>

            {/* Metric Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3.5">
              {/* Card 1: CPU Utilization */}
              <div className="bg-gray-50/50 dark:bg-[#0c1322]/90 border border-gray-200/80 dark:border-gray-800/90 rounded-xl p-3.5 shadow-sm hover:border-gray-300 dark:hover:border-gray-700/80 transition-all flex flex-col justify-start">
                <div>
                  <div className="flex items-center justify-between gap-2 mb-2.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="p-1.5 rounded-lg bg-sky-50 dark:bg-sky-500/10 text-sky-600 dark:text-sky-400 border border-sky-200/50 dark:border-sky-500/20 shrink-0">
                        <Cpu className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <h4 className="text-xs font-bold text-gray-900 dark:text-white truncate">CPU Total</h4>
                        <p className="text-[10px] text-gray-500 dark:text-gray-100 truncate">Processor Utilization</p>
                      </div>
                    </div>
                    <span className="text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded bg-sky-50 dark:bg-sky-500/10 text-sky-700 dark:text-sky-400 border border-sky-200 dark:border-sky-500/20 shrink-0">
                      Host CPU
                    </span>
                  </div>

                  {/* Hero 3-col values */}
                  <div className="grid grid-cols-3 gap-1 py-1 text-center min-h-[44px] items-center">
                    <div>
                      <div className="text-[10px] text-gray-500 dark:text-gray-100 font-semibold uppercase tracking-wider">Peak</div>
                      <div className="text-base sm:text-lg font-bold font-mono text-rose-500 dark:text-rose-400 truncate">
                        {peakCpuTotal != null ? `${peakCpuTotal.toFixed(1)}%` : '—'}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] text-gray-500 dark:text-gray-100 font-semibold uppercase tracking-wider">Avg</div>
                      <div className="text-base sm:text-lg font-bold font-mono text-gray-900 dark:text-gray-100 truncate">
                        {avgCpuTotal != null ? `${avgCpuTotal.toFixed(1)}%` : '—'}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] text-gray-500 dark:text-gray-100 font-semibold uppercase tracking-wider">Latest</div>
                      <div className="text-base sm:text-lg font-bold font-mono text-sky-500 dark:text-sky-400 truncate">
                        {latestCpuTotal != null ? `${latestCpuTotal.toFixed(1)}%` : '—'}
                      </div>
                    </div>
                  </div>

                  {/* CPU Multi-segment horizontal bar */}
                  <div className="mt-2.5 mb-1.5 space-y-1">
                    <div className="flex items-center justify-between text-[10px] font-mono h-4">
                      <span className="text-gray-500 dark:text-gray-100 truncate pr-1">
                        Active: {latestCpuTotal != null ? `${latestCpuTotal.toFixed(1)}%` : '0%'}
                      </span>
                      <span className="text-sky-500 dark:text-sky-400 font-semibold shrink-0">
                        {latestCpuTotal != null ? `${Math.max(100 - latestCpuTotal, 0).toFixed(1)}% Idle` : '—'}
                      </span>
                    </div>
                    <div className="h-1.5 w-full bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden flex">
                      <div
                        className="h-full bg-sky-400 transition-all duration-300"
                        style={{ width: `${Math.min(Math.max(Number(latest?.host_cpu_user_pct) || 0, 0), 100)}%` }}
                        title={`User CPU: ${Number(latest?.host_cpu_user_pct || 0).toFixed(1)}%`}
                      />
                      <div
                        className="h-full bg-purple-500 transition-all duration-300"
                        style={{ width: `${Math.min(Math.max(Number(latest?.host_cpu_sys_pct) || 0, 0), 100)}%` }}
                        title={`System CPU: ${Number(latest?.host_cpu_sys_pct || 0).toFixed(1)}%`}
                      />
                      <div
                        className="h-full bg-amber-400 transition-all duration-300"
                        style={{ width: `${Math.min(Math.max(Number(latest?.host_cpu_iowait_pct) || 0, 0), 100)}%` }}
                        title={`I/O Wait: ${Number(latest?.host_cpu_iowait_pct || 0).toFixed(1)}%`}
                      />
                      <div
                        className="h-full bg-rose-400 transition-all duration-300"
                        style={{ width: `${Math.min(Math.max(Number(latest?.host_cpu_steal_pct) || 0, 0), 100)}%` }}
                        title={`Steal CPU: ${Number(latest?.host_cpu_steal_pct || 0).toFixed(1)}%`}
                      />
                    </div>
                  </div>
                </div>

                {/* Sub-breakdown */}
                <div className="mt-3 pt-2.5 border-t border-gray-200/60 dark:border-gray-800 space-y-1.5">
                  <div className="flex items-center justify-between text-[10px] font-semibold text-gray-500 dark:text-gray-100 uppercase tracking-wider px-1">
                    <span>Breakdown</span>
                    <div className="grid grid-cols-3 gap-1 text-right w-40 sm:w-44 font-mono">
                      <span>Peak</span>
                      <span>Avg</span>
                      <span>Latest</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-[11px] px-1 py-0.5 rounded hover:bg-white dark:hover:bg-gray-800/50 transition-colors">
                    <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300 font-sans truncate pr-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-sky-400 shrink-0" />
                      User CPU
                    </span>
                    <div className="grid grid-cols-3 gap-1 text-right w-40 sm:w-44 font-mono text-[11px]">
                      <span className="text-rose-500 dark:text-rose-400 font-semibold">{peak?.host_cpu_user_pct != null ? `${Number(peak.host_cpu_user_pct).toFixed(1)}%` : '—'}</span>
                      <span className="text-gray-600 dark:text-gray-100">{average?.host_cpu_user_pct != null ? `${Number(average.host_cpu_user_pct).toFixed(1)}%` : '—'}</span>
                      <span className="text-gray-900 dark:text-white font-medium">{latest?.host_cpu_user_pct != null ? `${Number(latest.host_cpu_user_pct).toFixed(1)}%` : '—'}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-[11px] px-1 py-0.5 rounded hover:bg-white dark:hover:bg-gray-800/50 transition-colors">
                    <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300 font-sans truncate pr-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-purple-500 shrink-0" />
                      System CPU
                    </span>
                    <div className="grid grid-cols-3 gap-1 text-right w-40 sm:w-44 font-mono text-[11px]">
                      <span className="text-rose-500 dark:text-rose-400 font-semibold">{peak?.host_cpu_sys_pct != null ? `${Number(peak.host_cpu_sys_pct).toFixed(1)}%` : '—'}</span>
                      <span className="text-gray-600 dark:text-gray-100">{average?.host_cpu_sys_pct != null ? `${Number(average.host_cpu_sys_pct).toFixed(1)}%` : '—'}</span>
                      <span className="text-gray-900 dark:text-white font-medium">{latest?.host_cpu_sys_pct != null ? `${Number(latest.host_cpu_sys_pct).toFixed(1)}%` : '—'}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-[11px] px-1 py-0.5 rounded hover:bg-white dark:hover:bg-gray-800/50 transition-colors">
                    <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300 font-sans truncate pr-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
                      I/O Wait
                    </span>
                    <div className="grid grid-cols-3 gap-1 text-right w-40 sm:w-44 font-mono text-[11px]">
                      <span className="text-rose-500 dark:text-rose-400 font-semibold">{peak?.host_cpu_iowait_pct != null ? `${Number(peak.host_cpu_iowait_pct).toFixed(1)}%` : '—'}</span>
                      <span className="text-gray-600 dark:text-gray-100">{average?.host_cpu_iowait_pct != null ? `${Number(average.host_cpu_iowait_pct).toFixed(1)}%` : '—'}</span>
                      <span className="text-gray-900 dark:text-white font-medium">{latest?.host_cpu_iowait_pct != null ? `${Number(latest.host_cpu_iowait_pct).toFixed(1)}%` : '—'}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-[11px] px-1 py-0.5 rounded hover:bg-white dark:hover:bg-gray-800/50 transition-colors">
                    <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300 font-sans truncate pr-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-rose-400 shrink-0" />
                      Steal CPU
                    </span>
                    <div className="grid grid-cols-3 gap-1 text-right w-40 sm:w-44 font-mono text-[11px]">
                      <span className="text-rose-500 dark:text-rose-400 font-semibold">{peak?.host_cpu_steal_pct != null ? `${Number(peak.host_cpu_steal_pct).toFixed(1)}%` : '—'}</span>
                      <span className="text-gray-600 dark:text-gray-100">{average?.host_cpu_steal_pct != null ? `${Number(average.host_cpu_steal_pct).toFixed(1)}%` : '—'}</span>
                      <span className="text-gray-900 dark:text-white font-medium">{latest?.host_cpu_steal_pct != null ? `${Number(latest.host_cpu_steal_pct).toFixed(1)}%` : '—'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 2: Memory Usage */}
              <div className="bg-gray-50/50 dark:bg-[#0c1322]/90 border border-gray-200/80 dark:border-gray-800/90 rounded-xl p-3.5 shadow-sm hover:border-gray-300 dark:hover:border-gray-700/80 transition-all flex flex-col justify-start">
                <div>
                  <div className="flex items-center justify-between gap-2 mb-2.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="p-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-200/50 dark:border-emerald-500/20 shrink-0">
                        <Server className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <h4 className="text-xs font-bold text-gray-900 dark:text-white truncate">Memory Used</h4>
                        <p className="text-[10px] text-gray-500 dark:text-gray-100 truncate">RAM Utilization</p>
                      </div>
                    </div>
                    <span className="text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20 shrink-0">
                      Host RAM
                    </span>
                  </div>

                  {/* Hero 3-col values */}
                  <div className="grid grid-cols-3 gap-1 py-1 text-center min-h-[44px] items-center">
                    <div>
                      <div className="text-[10px] text-gray-500 dark:text-gray-100 font-semibold uppercase tracking-wider">Peak</div>
                      <div className="text-base sm:text-lg font-bold font-mono text-rose-500 dark:text-rose-400 truncate">
                        {peak?.host_mem_used_pct != null ? `${Number(peak.host_mem_used_pct).toFixed(1)}%` : '—'}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] text-gray-500 dark:text-gray-100 font-semibold uppercase tracking-wider">Avg</div>
                      <div className="text-base sm:text-lg font-bold font-mono text-gray-900 dark:text-gray-100 truncate">
                        {average?.host_mem_used_pct != null ? `${Number(average.host_mem_used_pct).toFixed(1)}%` : '—'}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] text-gray-500 dark:text-gray-100 font-semibold uppercase tracking-wider">Latest</div>
                      <div className="text-base sm:text-lg font-bold font-mono text-emerald-500 dark:text-emerald-400 truncate">
                        {latest?.host_mem_used_pct != null ? `${Number(latest.host_mem_used_pct).toFixed(1)}%` : '—'}
                      </div>
                    </div>
                  </div>

                  {/* Status header & Progress bar */}
                  <div className="mt-2.5 mb-1.5 space-y-1">
                    <div className="flex items-center justify-between text-[10px] font-mono h-4">
                      <span className="text-gray-500 dark:text-gray-100 truncate pr-1">
                        {fmtSize(memUsedMb, units.host_mem_used_mb)} of {fmtSize(memTotalMb, units.host_mem_total_mb)}
                      </span>
                      <span className="text-emerald-500 dark:text-emerald-400 font-semibold shrink-0">
                        {memUsedPctVal.toFixed(1)}% Committed
                      </span>
                    </div>
                    <div className="h-1.5 w-full bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-emerald-400 rounded-full transition-all duration-300"
                        style={{ width: `${Math.min(Math.max(memUsedPctVal, 0), 100)}%` }}
                      />
                    </div>
                  </div>
                </div>

                {/* Sub-breakdown */}
                <div className="mt-3 pt-2.5 border-t border-gray-200/60 dark:border-gray-800 space-y-1.5">
                  <div className="flex items-center justify-between text-[10px] font-semibold text-gray-500 dark:text-gray-100 uppercase tracking-wider px-1">
                    <span>Breakdown</span>
                    <div className="grid grid-cols-3 gap-1 text-right w-40 sm:w-44 font-mono">
                      <span>Peak</span>
                      <span>Avg</span>
                      <span>Latest</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-[11px] px-1 py-0.5 rounded hover:bg-white dark:hover:bg-gray-800/50 transition-colors">
                    <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300 font-sans truncate pr-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
                      Used RAM
                    </span>
                    <div className="grid grid-cols-3 gap-1 text-right w-40 sm:w-44 font-mono text-[11px]">
                      <span className="text-rose-500 dark:text-rose-400 font-semibold">{fmtSize(peak?.host_mem_used_mb, units.host_mem_used_mb)}</span>
                      <span className="text-gray-600 dark:text-gray-100">{fmtSize(average?.host_mem_used_mb, units.host_mem_used_mb)}</span>
                      <span className="text-gray-900 dark:text-white font-medium">{fmtSize(latest?.host_mem_used_mb, units.host_mem_used_mb)}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-[11px] px-1 py-0.5 rounded hover:bg-white dark:hover:bg-gray-800/50 transition-colors">
                    <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300 font-sans truncate pr-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-gray-400 dark:bg-gray-500 shrink-0" />
                      Total RAM
                    </span>
                    <div className="grid grid-cols-3 gap-1 text-right w-40 sm:w-44 font-mono text-[11px]">
                      <span className="text-gray-700 dark:text-gray-300">{fmtSize(peak?.host_mem_total_mb, units.host_mem_total_mb)}</span>
                      <span className="text-gray-600 dark:text-gray-100">{fmtSize(average?.host_mem_total_mb, units.host_mem_total_mb)}</span>
                      <span className="text-gray-900 dark:text-white font-medium">{fmtSize(latest?.host_mem_total_mb, units.host_mem_total_mb)}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-[11px] px-1 py-0.5 rounded hover:bg-white dark:hover:bg-gray-800/50 transition-colors">
                    <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300 font-sans truncate pr-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-teal-400 shrink-0" />
                      Swap Used
                    </span>
                    <div className="grid grid-cols-3 gap-1 text-right w-40 sm:w-44 font-mono text-[11px]">
                      <span className="text-gray-700 dark:text-gray-300">{peak?.host_swap_used_mb != null ? `${peak.host_swap_used_mb} MB` : '0 MB'}</span>
                      <span className="text-gray-600 dark:text-gray-100">{average?.host_swap_used_mb != null ? `${average.host_swap_used_mb} MB` : '0 MB'}</span>
                      <span className="text-gray-900 dark:text-white font-medium">{latest?.host_swap_used_mb != null ? `${latest.host_swap_used_mb} MB` : '0 MB'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 3: Disk Storage */}
              <div className="bg-gray-50/50 dark:bg-[#0c1322]/90 border border-gray-200/80 dark:border-gray-800/90 rounded-xl p-3.5 shadow-sm hover:border-gray-300 dark:hover:border-gray-700/80 transition-all flex flex-col justify-start">
                <div>
                  <div className="flex items-center justify-between gap-2 mb-2.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="p-1.5 rounded-lg bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-200/50 dark:border-amber-500/20 shrink-0">
                        <HardDrive className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <h4 className="text-xs font-bold text-gray-900 dark:text-white truncate">Disk Used</h4>
                        <p className="text-[10px] text-gray-500 dark:text-gray-100 truncate">Storage Capacity</p>
                      </div>
                    </div>
                    <span className="text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-500/20 shrink-0">
                      Host Disk
                    </span>
                  </div>

                  {/* Hero 3-col values */}
                  <div className="grid grid-cols-3 gap-1 py-1 text-center min-h-[44px] items-center">
                    <div>
                      <div className="text-[10px] text-gray-500 dark:text-gray-100 font-semibold uppercase tracking-wider">Peak</div>
                      <div className="text-base sm:text-lg font-bold font-mono text-rose-500 dark:text-rose-400 truncate">
                        {peak?.disk_used_pct != null ? `${Number(peak.disk_used_pct).toFixed(1)}%` : '—'}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] text-gray-500 dark:text-gray-100 font-semibold uppercase tracking-wider">Avg</div>
                      <div className="text-base sm:text-lg font-bold font-mono text-gray-900 dark:text-gray-100 truncate">
                        {average?.disk_used_pct != null ? `${Number(average.disk_used_pct).toFixed(1)}%` : '—'}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] text-gray-500 dark:text-gray-100 font-semibold uppercase tracking-wider">Latest</div>
                      <div className="text-base sm:text-lg font-bold font-mono text-amber-500 dark:text-amber-400 truncate">
                        {latest?.disk_used_pct != null ? `${Number(latest.disk_used_pct).toFixed(1)}%` : '—'}
                      </div>
                    </div>
                  </div>

                  {/* Status header & Progress bar */}
                  <div className="mt-2.5 mb-1.5 space-y-1">
                    <div className="flex items-center justify-between text-[10px] font-mono h-4">
                      <span className="text-gray-500 dark:text-gray-100 truncate pr-1">
                        Free: ~{fmtSize(diskFreeMb)}
                      </span>
                      <span className={`font-semibold shrink-0 ${diskUsedPctVal >= 90 ? 'text-rose-500 dark:text-rose-400' : 'text-amber-500 dark:text-amber-400'}`}>
                        {diskThresholdLabel}
                      </span>
                    </div>
                    <div className="h-1.5 w-full bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-300 ${diskUsedPctVal >= 90 ? 'bg-rose-500' : 'bg-amber-500'}`}
                        style={{ width: `${Math.min(Math.max(diskUsedPctVal, 0), 100)}%` }}
                      />
                    </div>
                  </div>
                </div>

                {/* Sub-breakdown */}
                <div className="mt-3 pt-2.5 border-t border-gray-200/60 dark:border-gray-800 space-y-1.5">
                  <div className="flex items-center justify-between text-[10px] font-semibold text-gray-500 dark:text-gray-100 uppercase tracking-wider px-1">
                    <span>Breakdown</span>
                    <div className="grid grid-cols-3 gap-1 text-right w-40 sm:w-44 font-mono">
                      <span>Peak</span>
                      <span>Avg</span>
                      <span>Latest</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-[11px] px-1 py-0.5 rounded hover:bg-white dark:hover:bg-gray-800/50 transition-colors">
                    <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300 font-sans truncate pr-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                      Used Disk
                    </span>
                    <div className="grid grid-cols-3 gap-1 text-right w-40 sm:w-44 font-mono text-[11px]">
                      <span className="text-rose-500 dark:text-rose-400 font-semibold">{fmtSize(peak?.disk_used_mb)}</span>
                      <span className="text-gray-600 dark:text-gray-100">{fmtSize(average?.disk_used_mb)}</span>
                      <span className="text-gray-900 dark:text-white font-medium">{fmtSize(latest?.disk_used_mb)}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-[11px] px-1 py-0.5 rounded hover:bg-white dark:hover:bg-gray-800/50 transition-colors">
                    <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300 font-sans truncate pr-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-gray-400 dark:bg-gray-500 shrink-0" />
                      Total Disk
                    </span>
                    <div className="grid grid-cols-3 gap-1 text-right w-40 sm:w-44 font-mono text-[11px]">
                      <span className="text-gray-700 dark:text-gray-300">{fmtSize(peak?.disk_total_mb)}</span>
                      <span className="text-gray-600 dark:text-gray-100">{fmtSize(average?.disk_total_mb)}</span>
                      <span className="text-gray-900 dark:text-white font-medium">{fmtSize(latest?.disk_total_mb)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 4: Network Throughput */}
              <div className="bg-gray-50/50 dark:bg-[#0c1322]/90 border border-gray-200/80 dark:border-gray-800/90 rounded-xl p-3.5 shadow-sm hover:border-gray-300 dark:hover:border-gray-700/80 transition-all flex flex-col justify-start">
                <div>
                  <div className="flex items-center justify-between gap-2 mb-2.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="p-1.5 rounded-lg bg-purple-50 dark:bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-200/50 dark:border-purple-500/20 shrink-0">
                        <Wifi className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <h4 className="text-xs font-bold text-gray-900 dark:text-white truncate">Network I/O</h4>
                        <p className="text-[10px] text-gray-500 dark:text-gray-100 truncate">Total Throughput</p>
                      </div>
                    </div>
                    <span className="text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-400 border border-purple-200 dark:border-purple-500/20 shrink-0">
                      Network I/O
                    </span>
                  </div>

                  {/* Hero 3-col values */}
                  <div className="grid grid-cols-3 gap-1 py-1 text-center min-h-[44px] items-center">
                    <div>
                      <div className="text-[10px] text-gray-500 dark:text-gray-100 font-semibold uppercase tracking-wider">Peak</div>
                      <div
                        className="text-xs sm:text-sm font-bold font-mono text-rose-500 dark:text-rose-400 truncate whitespace-nowrap"
                        title={fmtNet(peakNetTotal, units.host_net_rx_bytes)}
                      >
                        {fmtNet(peakNetTotal, units.host_net_rx_bytes)}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] text-gray-500 dark:text-gray-100 font-semibold uppercase tracking-wider">Avg</div>
                      <div
                        className="text-xs sm:text-sm font-bold font-mono text-gray-900 dark:text-gray-100 truncate whitespace-nowrap"
                        title={fmtNet(avgNetTotal, units.host_net_rx_bytes)}
                      >
                        {fmtNet(avgNetTotal, units.host_net_rx_bytes)}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] text-gray-500 dark:text-gray-100 font-semibold uppercase tracking-wider">Latest</div>
                      <div
                        className="text-xs sm:text-sm font-bold font-mono text-purple-500 dark:text-purple-400 truncate whitespace-nowrap"
                        title={fmtNet(latestNetTotal, units.host_net_rx_bytes)}
                      >
                        {fmtNet(latestNetTotal, units.host_net_rx_bytes)}
                      </div>
                    </div>
                  </div>

                  {/* Network Rx/Tx Dual-segment horizontal bar */}
                  <div className="mt-2.5 mb-1.5 space-y-1">
                    <div className="flex items-center justify-between text-[10px] font-mono h-4">
                      <span className="text-purple-500 dark:text-purple-400 font-semibold truncate pr-1">
                        Rx: {fmtNet(latest?.host_net_rx_bytes, units.host_net_rx_bytes)}
                      </span>
                      <span className="text-fuchsia-500 dark:text-fuchsia-400 font-semibold shrink-0">
                        Tx: {fmtNet(latest?.host_net_tx_bytes, units.host_net_tx_bytes)}
                      </span>
                    </div>
                    <div className="h-1.5 w-full bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden flex">
                      <div
                        className="h-full bg-purple-500 transition-all duration-300"
                        style={{ width: `${rxBarPct}%` }}
                        title={`Rx (Recv): ${fmtNet(latest?.host_net_rx_bytes, units.host_net_rx_bytes)}`}
                      />
                      <div
                        className="h-full bg-fuchsia-500 transition-all duration-300"
                        style={{ width: `${txBarPct}%` }}
                        title={`Tx (Sent): ${fmtNet(latest?.host_net_tx_bytes, units.host_net_tx_bytes)}`}
                      />
                    </div>
                  </div>
                </div>

                {/* Sub-breakdown */}
                <div className="mt-3 pt-2.5 border-t border-gray-200/60 dark:border-gray-800 space-y-1.5">
                  <div className="flex items-center justify-between text-[10px] font-semibold text-gray-500 dark:text-gray-100 uppercase tracking-wider px-1">
                    <span>Breakdown</span>
                    <div className="grid grid-cols-3 gap-1 text-right w-40 sm:w-44 font-mono">
                      <span>Peak</span>
                      <span>Avg</span>
                      <span>Latest</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-[11px] px-1 py-0.5 rounded hover:bg-white dark:hover:bg-gray-800/50 transition-colors">
                    <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300 font-sans truncate pr-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-purple-500 shrink-0" />
                      Rx (Recv)
                    </span>
                    <div className="grid grid-cols-3 gap-1 text-right w-40 sm:w-44 font-mono text-[11px]">
                      <span className="text-rose-500 dark:text-rose-400 font-semibold">{fmtNet(peak?.host_net_rx_bytes, units.host_net_rx_bytes)}</span>
                      <span className="text-gray-600 dark:text-gray-100">{fmtNet(average?.host_net_rx_bytes, units.host_net_rx_bytes)}</span>
                      <span className="text-gray-900 dark:text-white font-medium">{fmtNet(latest?.host_net_rx_bytes, units.host_net_rx_bytes)}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-[11px] px-1 py-0.5 rounded hover:bg-white dark:hover:bg-gray-800/50 transition-colors">
                    <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300 font-sans truncate pr-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-fuchsia-500 shrink-0" />
                      Tx (Sent)
                    </span>
                    <div className="grid grid-cols-3 gap-1 text-right w-40 sm:w-44 font-mono text-[11px]">
                      <span className="text-rose-500 dark:text-rose-400 font-semibold">{fmtNet(peak?.host_net_tx_bytes, units.host_net_tx_bytes)}</span>
                      <span className="text-gray-600 dark:text-gray-100">{fmtNet(average?.host_net_tx_bytes, units.host_net_tx_bytes)}</span>
                      <span className="text-gray-900 dark:text-white font-medium">{fmtNet(latest?.host_net_tx_bytes, units.host_net_tx_bytes)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Host Row 1: CPU Utilization & Memory */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl">
          <div>
            <div className="flex items-center gap-2 mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2">
              <Cpu className="w-4 h-4 text-blue-500 dark:text-blue-400" />
              <h4 className="text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">CPU Utilization %</h4>
            </div>
            <div className="h-40 w-full flex items-center justify-center">
              {hasData(formattedData) ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={formattedData}>
                    <defs>
                      <linearGradient id="colorUserCpu" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                    <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                    <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} domain={[0, 100]} ticks={[0, 50, 100]} />
                    <Tooltip content={<ChartTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                    <Legend content={<ScrollableLegend hoveredSeries={hoveredCpu} onHoverSeries={setHoveredCpu} />} />
                    <Area
                      type="linear"
                      dataKey="user_cpu"
                      name="User CPU %"
                      stroke="#3b82f6"
                      strokeOpacity={getOpacity(hoveredCpu, 'user_cpu')}
                      fillOpacity={getOpacity(hoveredCpu, 'user_cpu') * 0.4}
                      fill="url(#colorUserCpu)"
                      strokeWidth={hoveredCpu === 'user_cpu' ? 2.5 : 1.5}
                      dot={false}
                    />
                    <Line
                      type="linear"
                      dataKey="sys_cpu"
                      name="System CPU %"
                      stroke="#ef4444"
                      strokeOpacity={getOpacity(hoveredCpu, 'sys_cpu')}
                      strokeWidth={hoveredCpu === 'sys_cpu' ? 2.5 : 1.5}
                      dot={false}
                    />
                    <Line
                      type="linear"
                      dataKey="iowait_cpu"
                      name="I/O Wait CPU %"
                      stroke="#f59e0b"
                      strokeOpacity={getOpacity(hoveredCpu, 'iowait_cpu')}
                      strokeWidth={hoveredCpu === 'iowait_cpu' ? 2.5 : 1.5}
                      dot={false}
                    />
                    <Line
                      type="linear"
                      dataKey="steal_cpu"
                      name="Steal CPU %"
                      stroke="#a855f7"
                      strokeOpacity={getOpacity(hoveredCpu, 'steal_cpu')}
                      strokeWidth={hoveredCpu === 'steal_cpu' ? 2.5 : 1.5}
                      dot={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <span className="text-xs text-gray-500 italic">No CPU metric telemetry available</span>
              )}
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2">
              <Server className="w-4 h-4 text-emerald-500 dark:text-green-400" />
              <h4 className="text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">Memory & Swap</h4>
            </div>
            <div className="h-40 w-full flex items-center justify-center">
              {hasData(formattedData) ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={formattedData}>
                    <defs>
                      <linearGradient id="colorMem" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0.0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                    <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                    <YAxis yAxisId="pct" stroke="#9ca3af" fontSize={10} tickLine={false} domain={[0, 100]} ticks={[0, 50, 100]} tickFormatter={(v) => `${v}%`} />
                    <YAxis yAxisId="mb" orientation="right" stroke="#9ca3af" fontSize={10} tickLine={false} tickFormatter={(v) => `${v}`}>
                      <Label
                        value="GB"
                        angle={90}
                        position="insideRight"
                        offset={30}
                        fill="#9ca3af"
                        fontSize={11}
                      />
                    </YAxis>
                    <Tooltip content={<ChartTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                    <Legend content={<ScrollableLegend hoveredSeries={hoveredMem} onHoverSeries={setHoveredMem} />} />
                    <Area
                      yAxisId="pct"
                      type="monotone"
                      dataKey="mem_used"
                      name="Memory Used %"
                      stroke="#10b981"
                      strokeOpacity={getOpacity(hoveredMem, 'mem_used')}
                      fillOpacity={getOpacity(hoveredMem, 'mem_used') * 0.4}
                      fill="url(#colorMem)"
                      strokeWidth={hoveredMem === 'mem_used' ? 2.5 : 1.5}
                      dot={false}
                    />
                    <Line
                      yAxisId="mb"
                      type="monotone"
                      dataKey="mem_total_mb"
                      name="Total Memory (GB)"
                      stroke="#3b82f6"
                      strokeOpacity={getOpacity(hoveredMem, 'mem_total_mb')}
                      strokeWidth={hoveredMem === 'mem_total_mb' ? 2.5 : 1.5}
                      dot={false}
                    />
                    <Line
                      yAxisId="mb"
                      type="monotone"
                      dataKey="swap_used_mb"
                      name="Swap Used (GB)"
                      stroke="#f59e0b"
                      strokeOpacity={getOpacity(hoveredMem, 'swap_used_mb')}
                      strokeWidth={hoveredMem === 'swap_used_mb' ? 2.5 : 1.5}
                      dot={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <span className="text-xs text-gray-500 italic">No memory metric telemetry available</span>
              )}
            </div>
          </div>
        </div>

        {/* Host Row 3: Network & Disk */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl">
          <div>
            <div className="flex items-center gap-2 mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2">
              <Wifi className="w-4 h-4 text-purple-500 dark:text-purple-400" />
              <h4 className="text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">Network Throughput (MB/s)</h4>
            </div>
            <div className="h-40 w-full flex items-center justify-center">
              {hasData(formattedData) ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={formattedData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                    <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                    <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3} tickFormatter={(v) => `${v}`}>
                      <Label
                        value="MB/s"
                        angle={-90}
                        position="insideLeft"
                        offset={20}
                        fill="#9ca3af"
                        fontSize={11}
                      /> </YAxis>
                    <Tooltip content={<ChartTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                    <Legend content={<ScrollableLegend hoveredSeries={hoveredNet} onHoverSeries={setHoveredNet} />} />
                    <Line
                      type="monotone"
                      dataKey="net_rx"
                      name="Received MB/s"
                      stroke="#3b82f6"
                      strokeOpacity={getOpacity(hoveredNet, 'net_rx')}
                      strokeWidth={hoveredNet === 'net_rx' ? 2.5 : 1.5}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="net_tx"
                      name="Sent MB/s"
                      stroke="#a855f7"
                      strokeOpacity={getOpacity(hoveredNet, 'net_tx')}
                      strokeWidth={hoveredNet === 'net_tx' ? 2.5 : 1.5}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <span className="text-xs text-gray-500 italic">No network metric telemetry available</span>
              )}
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2">
              <HardDrive className="w-4 h-4 text-amber-500 dark:text-amber-400" />
              <h4 className="text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">Disk Capacity (GB)</h4>
            </div>
            <div className="h-40 w-full flex items-center justify-center">
              {hasData(formattedData) ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={formattedData}>
                    <defs>
                      <linearGradient id="colorDisk" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                    <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                    <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3} tickFormatter={(v) => `${v}`}>
                      <Label
                        value="GB"
                        angle={-90}
                        position="insideLeft"
                        offset={20}
                        fill="#9ca3af"
                        fontSize={11}
                      />
                    </YAxis>
                    <Tooltip content={<ChartTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                    <Legend content={<ScrollableLegend hoveredSeries={hoveredDisk} onHoverSeries={setHoveredDisk} />} />
                    <Area
                      type="monotone"
                      dataKey="disk_used"
                      name="Disk Used (GB)"
                      stroke="#f59e0b"
                      strokeOpacity={getOpacity(hoveredDisk, 'disk_used')}
                      fillOpacity={getOpacity(hoveredDisk, 'disk_used') * 0.4}
                      fill="url(#colorDisk)"
                      strokeWidth={hoveredDisk === 'disk_used' ? 2.5 : 1.5}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="disk_total"
                      name="Disk Total (GB)"
                      stroke="#6b7280"
                      strokeOpacity={getOpacity(hoveredDisk, 'disk_total')}
                      strokeWidth={1}
                      strokeDasharray="4 4"
                      dot={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <span className="text-xs text-gray-500 italic">No disk usage telemetry available</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* {!isES && (
        <div className="border-t border-gray-800 pt-4">
          <ReplicationView
            activeCluster={activeCluster}
            timeRange={timeRange}
            clientId={clientId}
            refreshKey={refreshKey}
          />
        </div>
      )} */}
    </div>
  );
};

export default MetricsTab;
